<template>
  <!-- 루트 경로 리다이렉트 중에는 로딩 표시 -->
  <div v-if="isRootPath" class="root-loading">
    <v-progress-circular indeterminate color="primary" size="48" />
  </div>
  <component v-else :is="layout">
    <RouterView />
  </component>
  <GlobalDialog />
  <GlobalPlanDialog />
</template>

<style scoped>
.root-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
}
</style>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import FullLayout from '@/shared/layouts/full/FullLayout.vue';
import BlankLayout from '@/shared/layouts/blank/BlankLayout.vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import GlobalDialog from '@/components/shared/GlobalDialog.vue';
import GlobalPlanDialog from '@/components/shared/GlobalPlanDialog.vue';
import { isValidUserAppAuth } from '@/shared/utils/authSessionUtils';

const route = useRoute();
const router = useRouter();
const sessionStore = useSessionStore();

// 라우터 초기화 완료 여부 (초기 네비게이션 완료 전까지 로딩 표시)
const isRouterReady = ref(false);

// 세션 체크 인터벌 (30초)
const SESSION_CHECK_INTERVAL = 30000;
let sessionCheckTimer: ReturnType<typeof setInterval> | null = null;

// 재시도 설정
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 3000; // 3초
let consecutiveFailures = 0; // 연속 실패 횟수 추적

// 초기 네비게이션(루트 → 로그인/대시보드 리다이렉트) 완료 전까지 로딩 표시
// route.path === '/' 조건은 리다이렉트 후에도 스피너가 남는 무한 로딩을 유발하므로 사용하지 않음
const isRootPath = computed(() => !isRouterReady.value);

// 라우트 메타에 따라 레이아웃 선택
const layout = computed(() => {
  const layoutName = route.meta.layout as string;

  switch (layoutName) {
    case 'blank':
      return BlankLayout;
    case 'default':
    default:
      return FullLayout;
  }
});

// public 페이지 목록 (세션 정보 불필요)
const publicPages = [
  '/auth/login',
  '/auth/register',
  '/auth/signup',
  '/signup',
  '/tsign/signing',
  '/tsign/completed',
  '/setup-entity/application',
  '/setup-entity/due-diligence-public',
  '/custom-form/fill',
  '/d'
];

// public 페이지 여부 확인
const isPublicPage = (path: string): boolean => {
  // setup-entity/application 경로는 public으로 처리 (이메일 링크로 접근 가능)
  if (path.startsWith('/setup-entity/application')) {
    return true;
  }

  // CDD 딥링크 전용 경로는 public으로 처리
  if (path.startsWith('/setup-entity/due-diligence-public')) {
    return true;
  }

  // 딥링크 핸들러 경로 (/d/:linkCode)
  if (path.startsWith('/d/')) {
    return true;
  }

  // 커스텀 폼 공개 응답 페이지 (딥링크로 접근)
  if (path.startsWith('/custom-form/fill')) {
    return true;
  }

  return publicPages.some(pagePath => {
    if (path === pagePath) return true;
    if (path.startsWith(pagePath + '/')) return true;
    return false;
  });
};

// 세션 초기화 여부 확인 함수
const shouldInitializeSession = (path: string): boolean => {
  if (isPublicPage(path)) {
    console.log('🚫 Public page detected, skipping session init:', path);
    return false;
  }
  console.log('✅ Non-public page, will initialize session:', path);
  return true;
};

// 단일 세션 검증 시도
const attemptSessionVerification = async (): Promise<{ success: boolean; isNetworkError: boolean; status?: number }> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    const response = await fetch(`/api/auth/me?_t=${Date.now()}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate'
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      const data = await response.json();
      const isValid = isValidUserAppAuth(data);
      return { success: isValid, isNetworkError: false };
    }

    // 401 = 세션 무효 (재시도 불필요)
    if (response.status === 401) {
      return { success: false, isNetworkError: false, status: 401 };
    }

    // 502, 503, 504 = 서버 일시적 다운 (재시도 필요)
    if ([502, 503, 504].includes(response.status)) {
      return { success: false, isNetworkError: true, status: response.status };
    }

    return { success: false, isNetworkError: false, status: response.status };
  } catch (error) {
    // 네트워크 에러, 타임아웃 = 서버 연결 불가 (재시도 필요)
    console.warn('[Session Check] Network error:', error);
    return { success: false, isNetworkError: true };
  }
};

// 지연 함수
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// 백엔드 세션 검증 함수 (재시도 로직 포함)
const verifySessionWithBackend = async (): Promise<boolean> => {
  for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    const result = await attemptSessionVerification();

    // 성공 시 연속 실패 카운터 리셋
    if (result.success) {
      consecutiveFailures = 0;
      return true;
    }

    // 401 에러 = 세션이 실제로 무효함 (재시도 불필요)
    if (result.status === 401) {
      console.warn('[Session Check] Session is invalid (401), no retry needed');
      consecutiveFailures = 0;
      return false;
    }

    // 네트워크 에러가 아니면 재시도 불필요
    if (!result.isNetworkError) {
      console.warn('[Session Check] Non-network error, no retry');
      return false;
    }

    // 마지막 시도가 아니면 재시도
    if (attempt < MAX_RETRY_ATTEMPTS) {
      console.log(`[Session Check] Retry attempt ${attempt + 1}/${MAX_RETRY_ATTEMPTS} in ${RETRY_DELAY_MS}ms...`);
      await delay(RETRY_DELAY_MS);
    }
  }

  // 모든 재시도 실패
  consecutiveFailures++;
  console.warn(`[Session Check] All ${MAX_RETRY_ATTEMPTS} attempts failed. Consecutive failures: ${consecutiveFailures}`);

  // 연속 2번 이상 실패 시에만 로그아웃 처리 (약 1분 이상 서버 다운)
  // 첫 번째 실패는 일시적 문제일 수 있으므로 허용
  if (consecutiveFailures >= 2) {
    console.warn('[Session Check] Multiple consecutive failures, session may be invalid');
    return false;
  }

  // 첫 번째 연속 실패는 true 반환하여 로그아웃 방지
  console.log('[Session Check] First consecutive failure, keeping session (server may be restarting)');
  return true;
};

// 세션 유효성 검사 및 실패 시 로그인 페이지로 리다이렉트
const checkSessionAndRedirect = async () => {
  const currentPath = route.path;

  // public 페이지에서는 체크 안 함
  if (isPublicPage(currentPath)) {
    return;
  }

  console.log('[Session Check] Verifying session...');
  const isValid = await verifySessionWithBackend();

  if (!isValid) {
    console.warn('[Session Check] Session invalid or backend down, redirecting to login');

    // 세션 스토어 정리
    sessionStore.clearSession();
    sessionStore.isInitialized = false;

    // 로그인 페이지로 리다이렉트
    router.push('/auth/login');
  } else {
    console.log('[Session Check] Session is valid');
  }
};

// 주기적 세션 체크 시작
const startSessionCheck = () => {
  if (sessionCheckTimer) {
    clearInterval(sessionCheckTimer);
  }

  sessionCheckTimer = setInterval(() => {
    checkSessionAndRedirect();
  }, SESSION_CHECK_INTERVAL);

  console.log('[Session Check] Started periodic check (every 30s)');
};

// 주기적 세션 체크 중지
const stopSessionCheck = () => {
  if (sessionCheckTimer) {
    clearInterval(sessionCheckTimer);
    sessionCheckTimer = null;
    console.log('[Session Check] Stopped periodic check');
  }
};

// 초기 로드 완료 플래그 (마운트 직후 visibility change 무시)
let isInitialLoadComplete = false;

// 탭 가시성 변경 핸들러 (탭으로 돌아올 때 세션 체크)
const handleVisibilityChange = () => {
  // 초기 로드 중에는 무시 (마운트 직후 트리거 방지)
  if (!isInitialLoadComplete) {
    console.log('[Session Check] Skipping visibility check during initial load');
    return;
  }

  // public 페이지에서는 무시
  if (isPublicPage(route.path)) {
    console.log('[Session Check] Skipping visibility check on public page');
    return;
  }

  if (document.visibilityState === 'visible') {
    console.log('[Session Check] Tab became visible, checking session...');
    checkSessionAndRedirect();
  }
};

// 세션 초기화 함수
const initializeSessionIfNeeded = async () => {
  const currentPath = route.path;

  console.log('🔍 App.vue - checking session init:', {
    path: currentPath,
    shouldInit: shouldInitializeSession(currentPath)
  });

  if (shouldInitializeSession(currentPath)) {
    // 이미 초기화되었거나 세션이 유효한 경우 스킵
    const sessionInfo = sessionStore.getSessionInfo();
    if (!sessionInfo.sessionId || !sessionStore.getIsAuthenticated) {
      console.log('🚀 App.vue - initializing global session for:', currentPath);
      await sessionStore.initializeSession(currentPath);
      console.log('✅ Global session initialized:', {
        isAuthenticated: sessionStore.getIsAuthenticated,
        user: sessionStore.getUser,
        sessionId: sessionStore.getSessionId
      });
    } else {
      console.log('⏭️ App.vue - session already initialized, skipping');
    }
  } else {
    console.log('🚫 App.vue - skipping session init for public page:', currentPath);
  }
};

// 컴포넌트 마운트 시 세션 체크 시작
onMounted(async () => {
  const ROUTER_READY_TIMEOUT_MS = 8000;
  const readyTimeout = setTimeout(() => {
    if (!isRouterReady.value) {
      console.warn('[App] Router ready timeout — unblocking UI');
      isRouterReady.value = true;
      if (route.path === '/') {
        void router.replace('/auth/login');
      }
    }
  }, ROUTER_READY_TIMEOUT_MS);

  try {
    // 라우터 초기화 완료 대기 (초기 네비게이션 완료까지)
    await router.isReady();
  } finally {
    clearTimeout(readyTimeout);
  }

  // 가드 리다이렉트가 누락된 경우 루트에서 빠져나가기
  if (route.path === '/' || route.meta.isRootRedirect) {
    let dest = '/auth/login';
    try {
      const res = await fetch(`/api/auth/me?_t=${Date.now()}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const ok = isValidUserAppAuth(data);
        if (ok) dest = '/dashboard';
      }
    } catch {
      // 네트워크 오류 시 로그인으로
    }
    await router.replace(dest);
  }

  isRouterReady.value = true;
  console.log('[App] Router is ready, path:', route.path);

  // 탭 가시성 변경 리스너 등록
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // 라우터 준비 후 초기 세션 초기화 (public 페이지가 아닌 경우)
  await initializeSessionIfNeeded();

  // public 페이지가 아닌 경우에만 주기적 체크 시작
  if (!isPublicPage(route.path)) {
    startSessionCheck();
  }

  // 초기 로드 완료 플래그 설정 (1초 후 - visibility change 초기 트리거 방지)
  setTimeout(() => {
    isInitialLoadComplete = true;
  }, 1000);
});

// 컴포넌트 언마운트 시 정리
onUnmounted(() => {
  stopSessionCheck();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

// 라우트 변경 시 세션 초기화 체크 (public 페이지에서는 스킵)
watch(
  () => route.path,
  async (newPath, oldPath) => {
    // 라우터가 준비되지 않은 경우 스킵 (초기 로드 시)
    if (!isRouterReady.value) {
      console.log('[App] Router not ready yet, skipping session init watch');
      return;
    }

    // 첫 실행 시(oldPath가 undefined) 라우트가 완전히 설정될 때까지 더 길게 대기
    const delay = oldPath === undefined ? 100 : 10;
    await new Promise(resolve => setTimeout(resolve, delay));

    // 라우트가 설정된 후에만 실행
    await initializeSessionIfNeeded();

    // public 페이지로 이동하면 주기적 체크 중지, 아니면 시작
    if (isPublicPage(newPath)) {
      stopSessionCheck();
    } else {
      startSessionCheck();
    }
  },
  { immediate: true }
);
</script>
